import type { RootState } from '@core/redux/interface';
import { useAppDispatch } from '@core/redux/hooks';
import { store } from '@core/redux/store';
import { trenchSelectors, trenchThunks } from '@trenches/redux';
import type { AttributeRequest, Trench, SetTrenchAttribute, LayingType } from '@trenches/types';
import { viewer3dSelectors } from '@viewer3D/redux';
import { useSelector } from 'react-redux';
import { ATTRIBUTE_POINT_FACTOR, getTrenchRadiusForViewport } from '../../helper';

export interface ConnectProps {
  getCommonTrench: (pointA: string, pointB: string) => Trench | undefined;
  radius: number;
  setAttribute: (
    trenchId: string,
    fromPointId: string,
    toPointId: string,
    attributeRequest: AttributeRequest,
  ) => void;
}

export const useConnect = (): ConnectProps => {
  const cameraViewport = useSelector(viewer3dSelectors.cameraViewport);
  const radius = getTrenchRadiusForViewport(ATTRIBUTE_POINT_FACTOR, cameraViewport);
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);
  const dispatch = useAppDispatch();

  return {
    radius,
    getCommonTrench: (pointA, pointB) =>
      trenchSelectors.getByCommonPoints(pointA, pointB)(store.getState() as RootState),
    setAttribute: (trenchId, fromPointId, toPointId, attributeRequest) => {
      const body: SetTrenchAttribute = {
        fromPointId,
        toPointId,
        [attributeRequest.key]: attributeRequest.value,
      };

      if (attributeRequest.key === 'layingType') {
        const layingType = attributeRequest.value as LayingType | null;
        if (layingType === 'OVERGROUND') {
          body.surfaceType = 'NOT_APPLICABLE';
        }
        if (layingType) {
          if (
            [
              'SOIL_DISPLACEMENT',
              'PULL',
              'HORIZONTAL_DIRECTIONAL_DRILLING',
              'CLAMPED',
              'OVERGROUND',
            ].includes(layingType)
          ) {
            body.depth = 'NOT_APPLICABLE';
            body.width = 'NOT_APPLICABLE';
          } else if (['PLOUGH', 'LAYJET', 'MICRO_TRENCHING'].includes(layingType)) {
            body.width = 'SIZE_0_CM';
          }
        }
      }

      dispatch(
        trenchThunks.setTrenchAttribute({
          path: ['projects', projectId!, 'trenches', trenchId, 'attributes'],
          body,
        }),
      );
    },
  };
};
